/* eslint-disable @typescript-eslint/no-explicit-any */
import { Component, ReactNode } from 'react';
import * as Sentry from '@sentry/react';
import authLocalStorage from '@/features/auth/_shared/utils/auth-localstorage';
// import { toast } from 'react-toastify';
import { AlertTriangle, Home, RefreshCw } from 'lucide-react';
import Button from '../ui/_refactor/buttons/button';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
}

/**
 * Sentry Error Boundary Component
 * Catches JavaScript errors anywhere in the child component tree
 * and reports them to Sentry
 */
class SentryErrorBoundary extends Component<Props, State> {
  private timeoutId: NodeJS.Timeout | null = null;
  private toastShownForError: string | null = null;

  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  componentWillUnmount() {
    // Cleanup timeout to prevent memory leaks
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }
  }

  componentDidUpdate(prevProps: Props) {
    // Reset error state when children change (e.g., route navigation)
    // This allows the error boundary to recover when navigating to a new page
    if (this.state.hasError && prevProps.children !== this.props.children) {
      // Clear toast tracking when navigating to a new page
      this.toastShownForError = null;
      this.handleReset();
    }
  }

  static getDerivedStateFromError(error: Error): State {
    // Update state so the next render will show the fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: any) {
    // Create a unique key for this error to prevent duplicate handling
    const errorKey = `${error.message}-${error.stack?.slice(0, 100)}`;

    // Prevent duplicate error handling for the same error
    if (this.toastShownForError === errorKey) {
      return;
    }

    // Mark this error as processed to prevent global handlers from reporting it
    const processedErrors = (window as any).__sentryProcessedErrors;
    if (processedErrors) {
      processedErrors.add(errorKey);
    }

    // console.log('🚨 Error caught by error boundary:', error.message);
    // console.log('📄 Component Stack:', errorInfo.componentStack);
    // console.log('📍 Error Stack:', error.stack);

    // Extract component information from stack trace
    const componentStack = errorInfo.componentStack;
    const componentInfo = this.extractComponentInfo(componentStack, error.stack);

    // console.log('🔍 Extracted Component Info:', componentInfo);

    // Create a custom error name with component info
    const customError = new Error(error.message);
    customError.name = `ReactError_${componentInfo.componentName}_${error.name || 'UnknownError'}`;
    customError.stack = error.stack;

    // Get user info for context
    const userToken = authLocalStorage.getToken();
    const userInfo = authLocalStorage.getUser();

    // Show error toast notification only once per error
    // Mark this error as having shown toast to prevent duplicates
    this.toastShownForError = errorKey;

    // TOAST IMPLEMENTATION
    // // Use setTimeout to ensure toast shows after current error handling
    // if (this.timeoutId) {
    //   clearTimeout(this.timeoutId);
    // }

    // this.timeoutId = setTimeout(() => {
    //   // console.log('🔥 Attempting to show error toast...');
    //   try {
    //     toast.error('Something went wrong. Our team has been notified and is working to fix the issue.', {
    //       autoClose: 5000,
    //       icon: <CircleX className="text-error shrink-0 w-[20px] h-[20px]" />,
    //       bodyClassName: `font-['Inter'] text-xs z-10000`,
    //       toastId: `error-boundary-${errorKey}`, // Use toastId to prevent duplicate toasts
    //     });
    //     // console.log('✅ Toast called successfully, ID:', toastId);
    //   } catch (_toastError) {
    //     // console.error('❌ Toast error:', _toastError);
    //     alert('Error: Something went wrong. Our team has been notified.');
    //   }
    //   // Clear the timeout ID after execution
    //   this.timeoutId = null;
    // }, 100);

    // Log the error to Sentry with comprehensive context
    Sentry.captureException(customError, {
      tags: {
        error_source: 'custom_error_boundary',
        error_boundary: 'react',
        component_error: true,
        user_authenticated: !!userToken,
        error_type: error.name || 'UnknownError',
        failing_component: componentInfo.failingComponent,
        component_file: componentInfo.fileName,
        has_parent_components: componentInfo.parentComponents.length > 0,
      },
      contexts: {
        react: {
          componentStack: errorInfo.componentStack,
          errorBoundary: 'SentryErrorBoundary',
          originalErrorName: error.name,
        },
        component_location: {
          failing_component: componentInfo.failingComponent,
          component_name: componentInfo.componentName,
          file_name: componentInfo.fileName,
          file_path: componentInfo.componentPath,
          line_number: componentInfo.lineNumber,
          column_number: componentInfo.columnNumber,
          parent_components: componentInfo.parentComponents,
          component_hierarchy: componentInfo.parentComponents.join(' → '),
        },
        user_session: {
          has_token: !!userToken,
          user_id: userInfo?.userId || 'anonymous',
          user_role: userInfo?.role?.name || 'unknown',
          current_url: window.location.href,
          current_path: window.location.pathname,
          user_agent: navigator.userAgent,
          timestamp: new Date().toISOString(),
        },
        browser_info: {
          language: navigator.language,
          platform: navigator.platform,
          cookie_enabled: navigator.cookieEnabled,
          online: navigator.onLine,
          viewport: `${window.innerWidth}x${window.innerHeight}`,
          screen: `${window.screen.width}x${window.screen.height}`,
        },
        error_details: {
          error_message: error.message,
          error_stack: error.stack,
          component_stack: errorInfo.componentStack,
          props_count: errorInfo.componentStack ? errorInfo.componentStack.split('\n').length : 0,
        },
      },
      extra: {
        errorInfo,
        component_details: componentInfo,
        localStorage_keys: Object.keys(localStorage),
        sessionStorage_keys: Object.keys(sessionStorage),
        page_title: document.title,
        referrer: document.referrer || 'direct',
        full_component_stack: componentStack,
        raw_error_stack: error.stack,
      },
    });
  }

  /**
   * Extract component information from React component stack and error stack
   */
  extractComponentInfo = (componentStack: string, errorStack?: string) => {
    const componentInfo = {
      componentName: 'UnknownComponent',
      fileName: 'unknown',
      lineNumber: 'unknown',
      columnNumber: 'unknown',
      componentPath: 'unknown',
      failingComponent: 'unknown',
      parentComponents: [] as string[],
    };

    try {
      if (componentStack) {
        // console.log('🔍 Parsing component stack...');
        // Parse React component stack
        const lines = componentStack.split('\n').filter((line) => line.trim());
        // console.log('📄 Stack lines:', lines);

        if (lines.length > 0) {
          // First line usually contains the failing component
          const firstLine = lines[0].trim();
          // console.log('🎯 First line:', firstLine);

          // Try multiple patterns for component name extraction
          const patterns = [
            /in\s+(\w+)\s*\(/, // "in ComponentName ("
            /at\s+(\w+)\s*\(/, // "at ComponentName ("
            /(\w+)\s*\(/, // "ComponentName ("
            /^\s*(\w+)/, // Just the component name at start
            /in\s+([A-Z]\w*)/, // "in ComponentName" (capitals only)
            /at\s+([A-Z]\w*)/, // "at ComponentName" (capitals only)
            /([A-Z]\w*)/, // Any capitalized word (component convention)
          ];

          for (const pattern of patterns) {
            const componentMatch = firstLine.match(pattern);
            if (
              componentMatch &&
              componentMatch[1] &&
              componentMatch[1] !== 'Object' &&
              componentMatch[1] !== 'eval' &&
              componentMatch[1] !== 'anonymous'
            ) {
              componentInfo.failingComponent = componentMatch[1];
              componentInfo.componentName = componentMatch[1];
              // console.log('✅ Found component name:', componentMatch[1], 'using pattern:', pattern.source);
              break;
            }
          }

          // Extract file location with more flexible patterns
          const locationPatterns = [
            /\(at\s+([^:]+):(\d+):(\d+)\)/, // (at file.tsx:123:45)
            /\(([^:]+):(\d+):(\d+)\)/, // (file.tsx:123:45)
            /at\s+([^:]+):(\d+):(\d+)/, // at file.tsx:123:45
            /([^:\s]+\.tsx?):(\d+):(\d+)/, // file.tsx:123:45
            /\(\s*([^)]+\.tsx?):(\d+):(\d+)\)/, // ( file.tsx:123:45)
            /\s+at\s+([^:]+):(\d+):(\d+)/, // [space]at file.tsx:123:45
            /\s+([^:\s]+\.tsx?):(\d+):(\d+)/, // [space]file.tsx:123:45
            /([^:\s]+\.(tsx?|jsx?)):(\d+):(\d+)/, // file.tsx:123:45 (tsx/jsx)
            /"([^"]+\.tsx?)":(\d+):(\d+)/, // "file.tsx":123:45
            /'([^']+\.tsx?)':(\d+):(\d+)/, // 'file.tsx':123:45
          ];

          // console.log('🔍 Trying to extract location from first line...');
          for (let i = 0; i < locationPatterns.length; i++) {
            const pattern = locationPatterns[i];
            const locationMatch = firstLine.match(pattern);
            // console.log(`📍 Pattern ${i + 1}: ${pattern.source} ->`, locationMatch ? 'MATCH!' : 'no match');

            if (locationMatch) {
              const filePath = locationMatch[1];
              const lineNum = locationMatch[2] || locationMatch[3]; // Handle different capture groups
              const colNum = locationMatch[3] || locationMatch[4];

              componentInfo.fileName = filePath;
              componentInfo.lineNumber = lineNum;
              componentInfo.columnNumber = colNum;

              // Extract component path from file path
              const pathParts = filePath.split('/');
              componentInfo.componentPath = pathParts.slice(-3).join('/'); // Last 3 parts

              // console.log('✅ Found location:', {
              //   fileName: filePath,
              //   lineNumber: lineNum,
              //   columnNumber: colNum,
              //   componentPath: componentInfo.componentPath,
              // });
              break;
            }
          }

          // If no location found in first line, try other lines
          if (componentInfo.fileName === 'unknown' && lines.length > 1) {
            // console.log('🔍 No location in first line, trying other lines...');
            for (let i = 1; i < Math.min(lines.length, 3); i++) {
              const line = lines[i].trim();
              // console.log(`📄 Checking line ${i + 1}:`, line);

              for (const pattern of locationPatterns) {
                const locationMatch = line.match(pattern);
                if (locationMatch) {
                  const filePath = locationMatch[1];
                  const lineNum = locationMatch[2] || locationMatch[3];
                  const colNum = locationMatch[3] || locationMatch[4];

                  componentInfo.fileName = filePath;
                  componentInfo.lineNumber = lineNum;
                  componentInfo.columnNumber = colNum;

                  const pathParts = filePath.split('/');
                  componentInfo.componentPath = pathParts.slice(-3).join('/');

                  // console.log('✅ Found location in line', i + 1, ':', {
                  //   fileName: filePath,
                  //   lineNumber: lineNum,
                  //   columnNumber: colNum,
                  // });
                  break;
                }
              }

              if (componentInfo.fileName !== 'unknown') break;
            }
          }

          // Extract parent components with flexible patterns
          componentInfo.parentComponents = lines
            .slice(1, 6) // Get up to 5 parent components
            .map((line) => {
              const patterns = [
                /in\s+(\w+)/,
                /at\s+(\w+)/,
                /(\w+)\s*\(/,
                /([A-Z]\w*)/, // Any capitalized word
              ];

              for (const pattern of patterns) {
                const match = line.trim().match(pattern);
                if (match && match[1] && match[1] !== 'Object' && match[1] !== 'eval' && match[1] !== 'anonymous') {
                  return match[1];
                }
              }
              return null;
            })
            .filter(Boolean) as string[];
        }
      }

      // If component stack didn't provide good info, try error stack
      if (componentInfo.componentName === 'UnknownComponent' && errorStack) {
        // console.log('🔍 Trying error stack parsing...');
        const stackLines = errorStack.split('\n');

        for (const line of stackLines) {
          // console.log('📄 Stack line:', line);
          // Look for React component patterns in stack trace with multiple patterns
          const patterns = [
            /at\s+(\w+)\s+\(.*\/([^\/]+\.tsx?):(\d+):(\d+)\)/, // at Component (file.tsx:123:45)
            /at\s+(\w+)\s+\([^)]*\/([^\/\)]+):(\d+):(\d+)\)/, // More flexible file path
            /(\w+)@.*\/([^\/]+\.tsx?):(\d+):(\d+)/, // Firefox format
            /at\s+([A-Z]\w+)\s+\(/, // at ComponentName (
            /at\s+([A-Z]\w+)/, // at ComponentName
            /([A-Z]\w+)\s*\(.*\.tsx?:/, // ComponentName(file.tsx:
          ];

          for (const pattern of patterns) {
            const reactMatch = line.match(pattern);
            if (
              reactMatch &&
              reactMatch[1] &&
              reactMatch[1] !== 'Object' &&
              reactMatch[1] !== 'eval' &&
              reactMatch[1] !== 'anonymous' &&
              /^[A-Z]/.test(reactMatch[1])
            ) {
              // Must start with capital (React component convention)

              componentInfo.componentName = reactMatch[1];
              if (reactMatch[2]) componentInfo.fileName = reactMatch[2];
              if (reactMatch[3]) componentInfo.lineNumber = reactMatch[3];
              if (reactMatch[4]) componentInfo.columnNumber = reactMatch[4];

              // console.log('✅ Found from error stack:', reactMatch[1], 'in', reactMatch[2] || 'unknown file');
              break;
            }
          }

          if (componentInfo.componentName !== 'UnknownComponent') {
            break;
          }
        }
      }
    } catch (_parseError) {
      // If parsing fails, keep default values
      // console.warn('Failed to parse component info:', _parseError);
    }

    return componentInfo;
  };

  handleReset = () => {
    // Clear the toast tracking when resetting
    this.toastShownForError = null;
    this.setState({ hasError: false, error: undefined });
  };

  handleRetry = () => {
    // Reset the error state
    this.handleReset();
    // Refresh the page
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      // You can render any custom fallback UI
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Return the children (current page) instead of error UI
      // Don't auto-reset here to prevent infinite loops
      // The error will be reset when the component tree changes or user navigates
      return (
        <div className="flex items-center justify-center min-h-screen bg-background">
          <div className="text-center max-w-md mx-auto p-6">
            <div className="mb-4">
              <AlertTriangle className="h-16 w-16 text-[#CC0202] mx-auto" />
            </div>
            <h1 className="text-2xl font-bold text-[#25151E] mb-2">Something went wrong</h1>
            <p className="text-[#25151E] mb-6">We encountered an error while loading this page. Please try again.</p>
            {process.env.NODE_ENV === 'development' && this.state.error && (
              <details className="mb-4 text-left">
                <summary className="cursor-pointer text-sm text-muted-foreground hover:text-foreground">
                  Error Details
                </summary>
                <pre className="mt-2 p-3 bg-muted rounded text-xs overflow-auto">
                  {this.state.error.message}
                  {this.state.error.stack && `\n\n${this.state.error.stack}`}
                </pre>
              </details>
            )}
            <div className="flex gap-[16px] justify-center">
              <Button
                onClick={this.handleRetry}
                variant="primary"
                startIcon={<RefreshCw className="h-[16px] w-[16px] shrink-0 mr-[4px]" />}
              >
                Try Again
              </Button>
              <Button
                onClick={() => (window.location.href = '/')}
                variant="outline"
                startIcon={<Home className="h-[16px] w-[16px] shrink-0 mr-[4px]" />}
              >
                Go Home
              </Button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Export your custom error boundary directly (no double wrapping)
export default SentryErrorBoundary;
